import React, { useEffect, useMemo, useState } from 'react'
import { Select } from '@/components/ui/Select'
import { approvalsApi } from '@/lib/api'
import type { Approver } from '@/types/approvals'

/**
 * "Send to" picker for a Representative submitting a workflow or campaign:
 * the account's Owner/Admin/Managers, defaulting to the account owner (the
 * brief's "sensible default to reduce friction"). Fetches once on mount and
 * reports the default upward as soon as it is known, so a form can submit
 * without the user touching the control.
 */
interface Props {
  value: number | null
  onChange: (approverId: number) => void
  id?: string
  disabled?: boolean
  /** Approver to default to instead of the account owner (e.g. whoever the
   * previous request for this item went to). Ignored when that person is no
   * longer an eligible approver. */
  preferredId?: number | null
}

/** The id to preselect: the preferred approver when still eligible, else the account default. */
export function pickDefaultApproverId(
  list: Approver[],
  defaultId: number | null | undefined,
  preferredId?: number | null,
): number | null {
  if (preferredId != null && list.some(a => a.id === preferredId)) return preferredId
  return defaultId ?? list[0]?.id ?? null
}

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', manager: 'Manager' }

export function approverLabel(a: Approver): string {
  const name = a.full_name?.trim() || a.email
  const role = ROLE_LABEL[a.role] || a.role
  return `${name} (${role})`
}

const ApproverSelect: React.FC<Props> = ({ value, onChange, id, disabled, preferredId }) => {
  const [approvers, setApprovers] = useState<Approver[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    approvalsApi.approvers()
      .then(res => {
        if (cancelled) return
        const list = res.data?.approvers ?? []
        setApprovers(list)
        const fallback = pickDefaultApproverId(list, res.data?.default_approver_id, preferredId)
        if (value == null && fallback != null) onChange(fallback)
      })
      .catch(() => { /* the Select shows an empty state */ })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // Fetch once; `value`/`onChange` are read at resolution time only.
  }, [])

  const options = useMemo(
    () => approvers.map(a => ({ value: String(a.id), label: approverLabel(a), description: a.email })),
    [approvers],
  )

  return (
    <Select
      id={id}
      value={value != null ? String(value) : ''}
      onChange={v => onChange(Number(v))}
      options={options}
      placeholder={loading ? 'Loading approvers…' : approvers.length ? 'Choose an approver' : 'No approvers on this account'}
      disabled={disabled || loading || approvers.length === 0}
      ariaLabel={id ? undefined : 'Send to'}
    />
  )
}

export default ApproverSelect
